import { createPath, createURL_SSR, ROUTE } from "app-constants/routing"
import { ContentContainer } from "components/content-container"
import { ExhibitorsBrowseAll } from "components/exhibitors/ExhibitorsBrowseAll"
import { NoExhibitorsFallback } from "components/exhibitors/NoExhibitorsFallback"
import MainLayout from "components/main-layout"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import { getExhibitorsCopyText } from "lib/exhibitors/getExhibitorsCopyText"
import applyCache from "lib/mw/apply-cache"
import applyCurrentServerContext from "lib/mw/apply-current-server-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyTitle from "lib/mw/apply-title"
import useCurrent, { Current } from "lib/use-current"
import ConventionProviderExhibitor from "models/convention-api/v1/ConventionProviderExhibitor"
import ConventionProviderExhibitorCount from "models/convention-api/v1/ConventionProviderExhibitorCount"
import React from "react"
import tryToGetAppLogo from "utils/tryToGetAppLogo"

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentServerContext(),
  applyTitle(({ props }) => `Browse All ${getExhibitorsCopyText(props.current).plural}`),
  [
    async (context) => {
      await ConventionProviderExhibitor.applyOne("exhibitor", {
        filter: {
          disabled: "false",
          exclude_institution_id: context.props.current.config.institution_id,
        },
      })(context)
    },
  ],
  [
    async (context) => {
      if (!context.props.exhibitor) return

      await ConventionProviderExhibitor.applyMore("exhibitors", {
        limit: 20,
        filter: {
          disabled: "false",
          exclude_institution_id: context.props.current.config.institution_id,
        },
        // Default is ascending (a-z)
        sort: "institution.name",
        includes: ["institution", "institution.chatAvailable"],
      })(context)
    },
    async (context) => {
      if (!context.props.exhibitor) return

      await ConventionProviderExhibitorCount.applyOne("exhibitorsCount", {
        filter: {
          disabled: "false",
          exclude_institution_id: context.props.current.config.institution_id,
        },
      })(context)
    },
  ],
  applyNextSeo((context) => ({
    openGraph: {
      title: context.props.current?.provider?.name + " - Exhibitors",
      url: createURL_SSR(context, { path: ROUTE.EXHIBITORS_BROWSE_ALL }),
      type: "website",
      images: [tryToGetAppLogo(context.props.current)],
    },
  })),
)

interface ExhibitorsBrowseAllPageProps {
  exhibitor: ConventionProviderExhibitor
  exhibitors: ConventionProviderExhibitor[][]
  exhibitorsCount: ConventionProviderExhibitorCount
  current: Current
}

export default buildServerSidePage(function ExhibitorsBrowseAllPage(
  props: ExhibitorsBrowseAllPageProps,
) {
  const { exhibitor, exhibitors, exhibitorsCount } = props
  const current = useCurrent()

  return (
    <MainLayout layout="macro">
      <ContentContainer>
        <NoExhibitorsFallback hasExhibitors={!!exhibitor}>
          <ExhibitorsBrowseAll
            filter={{ exclude_institution_id: current?.config.institution_id }}
            withBackToExplore={current?.config?.explore_exhibitor_page}
            exhibitorCardHref={createPath({ path: ROUTE.EXHIBITORS })}
            fallbackDataExhibitors={exhibitors}
            fallbackDataExhibitorsCount={exhibitorsCount}
          />
        </NoExhibitorsFallback>
      </ContentContainer>
    </MainLayout>
  )
})
